import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material';
import { CustomerEditForm } from '../customers/CustomerEditForm';
import { useCustomerEditForm } from '../../hooks/useCustomerEditForm';
import type { EditableCustomerUpdates } from '../../services/customerUpdateApi';

interface EditCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  /** Dataverse customer GUID (quote.customerId). */
  customerId: string | null;
  /**
   * Called after a successful save so the parent can refetch the quote and
   * pick up the corrected customer name/contact details.
   */
  onSaved: () => void;
}

/**
 * Edit the customer attached to a quote directly from the quote detail page.
 * Reuses the shared customer-edit stack — `useCustomerEditForm` (loads via
 * GET /api/customers/{id}/edit, saves via PATCH /api/customers/{id}, which
 * mirrors changes into RepairShopr) + `CustomerEditForm` — so quick-quote
 * customers captured with missing details can be completed without leaving
 * the quote.
 */
export const EditCustomerDialog: React.FC<EditCustomerDialogProps> = ({
  open,
  onClose,
  customerId,
  onSaved,
}) => {
  const {
    customer,
    isLoading,
    isSaving,
    error,
    saveError,
    lastSaveResult,
    save,
  } = useCustomerEditForm(customerId, open);

  const handleSave = async (updates: EditableCustomerUpdates) => {
    await save(updates);
    onSaved();
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && error && <Alert severity="error">{error}</Alert>}
        {!isLoading && !error && customer && (
          <CustomerEditForm
            customer={customer}
            isSaving={isSaving}
            saveError={saveError}
            lastSaveResult={lastSaveResult}
            onSave={handleSave}
            onCancel={onClose}
            mode="edit"
            title="Edit customer"
            submitLabel="Save customer"
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditCustomerDialog;
